import React, {
  useState, useEffect, useRef, useMemo,
} from 'react';
import {
  Container, Line, colors, Figure,
} from 'globalStyle';
import { Title } from 'components/Texts';
import { routes } from 'routes';
import { Redirect, useRoute, useLocation as useWouterLocation } from 'wouter';
import { ListOfProject } from 'components/Cards';
import { Navbar } from 'Layout/components/Navbar';
import {
  useMediaQuery,
  useSingleProject,
  useUser,
  useProjectsByStatus,
  useCategorizations,
  useSearchParam,
} from 'hooks';
import { useLocation } from 'hooks/useLocation';
import { projectStateType } from 'types';
import { Banner } from 'page/components/Banner';
import { Loading } from 'page/components/Loading';
import { Video } from 'page/components/Video';
import { Box, Grid } from '@material-ui/core';
import { Assets } from 'page/components/Assets';
import { Card } from 'page/components/Card';
import { CompleteProfileModal } from 'page/components/CompleteProfileModal';
import { InvertModal } from 'page/components/InvertModal';
import { Helmet } from 'react-helmet-async';
import InvestorSimulator from 'page/Profile/User/Simulator/InvestorSimulator';
import InvestmentConditions from 'page/components/InvestmentConditions/index';
import { SignUp } from 'components/Modals/SignUp';
import RegisterChecker from 'Layout/components/Navbar/components/Register/components/RegisterChecker';
import {
  colorByProjectType,
  typeColorByProjectType,
} from 'types/projectType';
import AboutProjectType from 'page/components/AboutProjectType';
import ProjectDescription from '../components/ProjectDescription';
import ProjectDetails from '../components/ProjectDetails';
import ProjectInfoCards from '../components/ProjectInfoCards';
import ProjectMeetCompany from '../components/ProjectMeetCompany';
import ProjectInvertButtons from '../components/ProjectInvertButtons';

const scrollTo = (height = 100) => {
  window.scrollTo({
    top: height,
    behavior: 'smooth',
  });
};

export const ProjectDesktop = () => {
  const isDesktop = useMediaQuery();
  const setions = useRef(null);
  const cardRef = useRef(null);
  const from = useSearchParam('from');
  const [, setRoute] = useWouterLocation();
  const descriptionTeamRiskRef = useRef(null);
  const [detailsActiveItem, setDetailsActiveItem] = useState();
  const [, params] = useRoute(routes.project.params);
  const {
    isLoadingSingleProject,
    isErrorSingleProject,
    singleProject,
  } = useSingleProject(params ?? {});
  const { isLoadingProjectsByStatus, projectsByStatus } = useProjectsByStatus({
    type: projectStateType.OPEN,
  });

  const { categorizations, defaultCategorization } = useCategorizations();
  const categorization = useMemo(() => {
    if (
      categorizations
      && singleProject
      && from === 'categorization'
      && singleProject?.categorizationId !== defaultCategorization?.id
    ) {
      return categorizations?.find(
        (v) => v?.id === singleProject?.categorizationId,
      );
    }
    return undefined;
  }, [categorizations, singleProject]);
  const [currentAmount, setCurrentAmount] = useState(0);
  const [maxAmount, setMaxAmount] = useState(0);
  const { isLogin, user } = useUser();
  const [isOpenModal, setOpenModal] = useState({
    signUp: false,
    completeProfile: false,
    investor: false,
    completeProfileQR: false,
  });
  const [showInvestorSimulator, setShowInvestorSimulator] = useState(false);
  const { location } = useLocation();
  const videoProject = useMemo(
    () => (singleProject?.videoUrl
      ? () => (
        <Video
          isDesktop
          url={singleProject?.videoUrl}
          onPlay={() => cardRef.current.isVisible(false)}
          onPause={() => cardRef.current.isVisible(true)}
          onEnded={() => cardRef.current.isVisible(true)}
        />
      )
      : null),
    [singleProject],
  );
  useEffect(() => {
    setCurrentAmount(singleProject?.currentAmount);
    setMaxAmount(singleProject?.maxAmount);
  }, [singleProject?.currentAmount]);

  const handleGoRisks = () => {
    scrollTo(descriptionTeamRiskRef.current?.offsetTop);
    setDetailsActiveItem('risks');
  };
  useEffect(() => {
    if (location.previous === '/perfil/inversiones') {
      handleGoRisks();
    }
  }, [isLoadingProjectsByStatus]);

  const handleInvertModal = () => {
    setShowInvestorSimulator(false);
    let state = {};

    if (!isLogin) {
      state = { ...isOpenModal, signUp: true };
    } else if (user.info.status === 'PENDING_FORMS') {
      state = { ...isOpenModal, completeProfile: true };
    } else {
      state = { ...isOpenModal, investor: true };
    }
    setOpenModal(state);
  };

  const handleCloseModal = () => {
    setOpenModal({
      signUp: false,
      completeProfile: false,
      investor: false,
      completeProfileQR: false,
    });
  };

  const handlerSimulator = () => {
    setShowInvestorSimulator(!showInvestorSimulator);
  };

  if (isLoadingSingleProject || !singleProject) return <Loading />;
  if (isErrorSingleProject) return <Redirect to="/404" />;

  return (
    <div>
      <Navbar
        buttonsColor={categorization?.color}
        customLogo={
          categorization && (
            <Figure
              ml={!isDesktop && 38}
              width={isDesktop ? '189px' : '130px'}
              onClick={() => setRoute(routes.invert)}
              maxHeight={isDesktop ? '70px' : '30px'}
              style={{ position: 'relative', cursor: 'pointer' }}
            >
              <img
                alt="logo"
                src={categorization?.logo.temporalResource}
                style={{
                  maxHeight: '60px',
                  objectFit: 'scale-down',
                  objectPosition: 'center center',
                }}
              />
            </Figure>
          )
        }
      />
      <Helmet>
        <meta name="description" content={singleProject.shortDescription} />
      </Helmet>
      <Box>
        <Assets
          isDesktop
          categorization={categorization}
          photoProduct={singleProject?.images}
          onClick={() => {
            scrollTo(setions.current?.offsetTop);
          }}
          video={videoProject}
        >
          <Card
            isDesktop
            ref={cardRef}
            color={categorization?.color}
            projectName={singleProject?.projectName}
            statusProject={singleProject?.status}
            companyName={singleProject?.companyName}
            shortDescription={singleProject?.shortDescription}
            currentAmount={currentAmount}
            maxAmount={maxAmount}
            percent={singleProject?.percent}
            endDate={singleProject?.endDate}
            currentInvestorsAmount={singleProject?.currentInvestorsAmount}
            timeStimated={singleProject?.timeStimated}
            closureDate={singleProject?.closureDate}
            isOpen={singleProject?.status === projectStateType.OPEN}
            onClick={handleInvertModal}
            daysLeft={singleProject?.daysLeft}
            projectInfo={singleProject}
            position="absolute"
            transform=""
            top=""
            handlerSimulator={handlerSimulator}
          />
        </Assets>
      </Box>
      <ProjectDescription
        isDesktop
        color={categorization?.color}
        singleProject={singleProject}
      />
      <Container pt={80} sm="75%" md="75%" lg="60%">
        <Banner loading={isLoadingSingleProject} userData={user?.info} />
      </Container>

      <Grid item xs={12} sm={12} ref={setions}>
        <Title isCenter size={30}>
          Condiciones de inversión
        </Title>
      </Grid>
      <Container xl="60%" mb={5} mt={50}>
        <InvestmentConditions
          color={categorization?.color}
          project={singleProject}
        />
      </Container>
      <Box marginTop="40px" marginBottom="48px">
        <ProjectInvertButtons
          color={categorization?.color}
          variant={typeColorByProjectType[singleProject?.projectType]}
          isDesktop
          onInvertClick={handleInvertModal}
          onSimulateClick={() => setShowInvestorSimulator(true)}
          {...singleProject}
        />
      </Box>
      <AboutProjectType
        color={categorization?.color}
        projectType={singleProject?.projectType}
        id={singleProject?.projectId}
      />
      <Box
        paddingTop="50px"
        paddingLeft="30px"
        paddingRight="30px"
        paddingBottom="120px"
        bgcolor={colors.background.GRAY_3}
      >
        <Grid item xs={12} ref={descriptionTeamRiskRef}>
          <Title isCenter size={30} mb={60}>
            Conoce el proyecto
          </Title>
        </Grid>
        <ProjectDetails
          {...singleProject}
          isDesktop={isDesktop}
          color={categorization?.color}
          activeItem={detailsActiveItem}
          clearActiveItem={() => setDetailsActiveItem()}
        />
      </Box>
      <ProjectMeetCompany
        color={categorization?.color}
        {...singleProject}
        isDesktop={isDesktop}
      />
      <Box marginTop="75px" marginBottom="90px">
        <ProjectInvertButtons
          variant={typeColorByProjectType[singleProject?.projectType]}
          isDesktop
          color={categorization?.color}
          onInvertClick={handleInvertModal}
          onSimulateClick={() => setShowInvestorSimulator(true)}
          {...singleProject}
        />
      </Box>
      <Grid container justify="center">
        <Box width="83%" marginBottom="90px">
          <ProjectInfoCards
            isDesktop
            color={
              categorization?.color
              || colorByProjectType[singleProject?.projectType]
            }
          />
        </Box>
      </Grid>
      <Container md="60%">
        <Line top={40} />
      </Container>
      {projectsByStatus.filter(
        (project) => project?.projectId !== singleProject?.projectId
          && project?.maxAmount !== project?.currentAmount,
      ).filter((v) => v?.id !== singleProject?.projectId).length > 0 && (
        <Container mt={80} mb={80} md="60%" lg="60%" align="center">
          <Title number={3} isCenter mb={40}>
            También te puede interesar
          </Title>
          <ListOfProject
            isDesktop={isDesktop}
            isLoading={isLoadingProjectsByStatus}
            useCategorizationColors={from === 'categorization'}
            projects={projectsByStatus.filter(
              (project) => project?.projectId !== singleProject?.projectId
                && project?.maxAmount !== project?.currentAmount,
            )}
          />
        </Container>
      )}

      <SignUp isOpen={isOpenModal.signUp} onClick={handleCloseModal} />

      <CompleteProfileModal
        isOpen={isOpenModal.completeProfile}
        isLogin={isLogin}
        onClick={handleCloseModal}
      />
      {isOpenModal.investor && (
        <RegisterChecker
          check
          onClose={handleCloseModal}
        >
          <InvertModal
            userData={user?.info}
            currentAmount={currentAmount}
            goRisk={() => {
              handleGoRisks();
            }}
            onSuccessfulInvert={(amount) => {
              setCurrentAmount(amount);
            }}
            isLogin={isLogin}
            onChange={handleCloseModal}
            onClick={handleCloseModal}
            project={singleProject}
          />
        </RegisterChecker>

      )}
      {showInvestorSimulator && (
        <InvestorSimulator
          handleClose={handlerSimulator}
          project={singleProject}
          handleInvert={handleInvertModal}
        />
      )}
    </div>
  );
};
